import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref } from "firebase/storage";
import { getKajikajyAdminFirebaseClient } from "./firebaseClient";

export type SupportStatus = "open" | "in_progress" | "waiting_user" | "closed";

export interface SupportTicketSummary {
  readonly ticketId: string;
  readonly ownerUid: string;
  readonly category: string;
  readonly status: SupportStatus;
  readonly locale: string;
  readonly lastMessagePreview: string;
  readonly unreadByUser: boolean;
  readonly unreadForSupport: boolean;
  readonly requesterEmail: string | null;
  readonly requesterPhoneNumber: string | null;
  readonly requesterKind: "anonymous" | "registered";
  readonly createdAt: string | null;
  readonly updatedAt: string | null;
}

export interface SupportAttachment {
  readonly status: "ready";
  readonly contentType: string | null;
  readonly storagePath: string | null;
  readonly byteCount: number;
}

export interface SupportMessage {
  readonly messageId: string;
  readonly sender: "user" | "support";
  readonly body: string;
  readonly createdAt: string | null;
  readonly attachment: SupportAttachment | null;
}

export interface SupportTicketDetail {
  readonly ticket: SupportTicketSummary;
  readonly messages: readonly SupportMessage[];
}

function requireClient() {
  const client = getKajikajyAdminFirebaseClient();
  if (!client) {
    throw new Error("La configuration Firebase de la console Support est absente.");
  }
  return client;
}

export async function verifySupportSession(): Promise<string | null> {
  const client = requireClient();
  const callable = httpsCallable<Record<string, never>, { ok: boolean; email: string | null }>(
    client.functions,
    "getSupportAdminSession"
  );
  const response = await callable({});
  return response.data.email;
}

export async function claimSupportAgentAccess(): Promise<string | null> {
  const client = requireClient();
  const callable = httpsCallable<Record<string, never>, { ok: boolean; email: string | null }>(
    client.functions,
    "claimSupportAgentAccess"
  );
  const response = await callable({});
  return response.data.email;
}

export async function listSupportTickets(
  status: SupportStatus | "all"
): Promise<readonly SupportTicketSummary[]> {
  const client = requireClient();
  const callable = httpsCallable<
    { status: SupportStatus | "all"; limit: number },
    { tickets: SupportTicketSummary[] }
  >(client.functions, "listSupportTickets");
  const response = await callable({ status, limit: 100 });
  return response.data.tickets;
}

export async function getSupportTicket(
  ownerUid: string,
  ticketId: string
): Promise<SupportTicketDetail> {
  const client = requireClient();
  const callable = httpsCallable<
    { ownerUid: string; ticketId: string },
    SupportTicketDetail
  >(client.functions, "getSupportTicket");
  const response = await callable({ ownerUid, ticketId });
  return response.data;
}

export async function replySupportTicket(
  ownerUid: string,
  ticketId: string,
  body: string
): Promise<void> {
  const client = requireClient();
  const callable = httpsCallable<
    { ownerUid: string; ticketId: string; body: string },
    { ok: boolean; messageId: string }
  >(client.functions, "replySupportTicket");
  await callable({ ownerUid, ticketId, body });
}

export async function setSupportTicketStatus(
  ownerUid: string,
  ticketId: string,
  status: SupportStatus
): Promise<void> {
  const client = requireClient();
  const callable = httpsCallable<
    { ownerUid: string; ticketId: string; status: SupportStatus },
    { ok: boolean }
  >(client.functions, "setSupportTicketStatus");
  await callable({ ownerUid, ticketId, status });
}

export async function getSupportAttachmentUrl(storagePath: string): Promise<string> {
  return getDownloadURL(ref(requireClient().storage, storagePath));
}
